// What a promo code redeem left behind, for the banner on the landing screen.
// One localStorage key. Written by Login.jsx and AuthCallback.jsx after
// POST /api/promo/redeem, read by components/PromoGrantBanner.jsx.
//
//   { code, days, expiresAt, status: 'granted' }   access was just granted
//   { code, status: 'alreadyRedeemed' }            the code was used before
//   either may carry dismissed: true               the banner was closed
import { normalizePromoCode } from '@/lib/promoCodes';

export const PROMO_GRANT_KEY = 'ttt_promo_grant';

const isObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const validDate = (iso) => typeof iso === 'string' && !Number.isNaN(new Date(iso).getTime());
const validDays = (d) => typeof d === 'number' && Number.isFinite(d) && d > 0;

const write = (value) => {
  try {
    if (value === null) window.localStorage.removeItem(PROMO_GRANT_KEY);
    else window.localStorage.setItem(PROMO_GRANT_KEY, JSON.stringify(value));
  } catch {
    /* storage blocked: the banner just does not show */
  }
};

const readRaw = () => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(PROMO_GRANT_KEY));
    return isObj(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

/** Store the outcome of a redeem. `result` is the JSON the server sent back. */
export function rememberPromoResult(code, result) {
  const c = normalizePromoCode(code);
  if (!c || !isObj(result)) return;
  if (result.alreadyActive) {
    // Nothing new was granted: no banner, and no stale note either.
    write(null);
    return;
  }
  if (result.success === true) {
    if (!validDays(result.days) || !validDate(result.expiresAt)) return;
    write({ code: c, days: result.days, expiresAt: result.expiresAt, status: 'granted' });
    return;
  }
  if (result.alreadyRedeemed === true) {
    // A second tab redeeming the same code gets "already used": keep the grant
    // the first one stored rather than replacing it with the note.
    const prev = readRaw();
    if (prev && prev.code === c && prev.status === 'granted' && validDate(prev.expiresAt) && new Date(prev.expiresAt) > new Date()) return;
    write({ code: c, status: 'alreadyRedeemed' });
  }
}

/** What the banner should show now, or null. */
export function readPromoGrant(now = new Date()) {
  const g = readRaw();
  if (!g || g.dismissed === true) return null;
  if (typeof g.code !== 'string' || g.code === '') return null;
  if (g.status === 'alreadyRedeemed') return { code: g.code, status: 'alreadyRedeemed' };
  if (g.status !== 'granted') return null;
  if (!validDays(g.days) || !validDate(g.expiresAt)) return null;
  if (new Date(g.expiresAt).getTime() <= now.getTime()) return null;
  return { code: g.code, days: g.days, expiresAt: g.expiresAt, status: 'granted' };
}

export function dismissPromoGrant() {
  const g = readRaw();
  if (g) write({ ...g, dismissed: true });
}
